"use client";
import React from "react";

import { Box, Target } from "../../foundations";
import { Text } from "../../foundations/Typography/Text";
import { useId } from "../../hooks/useId";

export interface BreadcrumbPage {
  title: string;
  pageId: string;
}

export interface BreadcrumbProps {
  pages: BreadcrumbPage[];
  onClickPage: (pageId: string) => void;
  expand?: boolean;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = (
  props: BreadcrumbProps
) => {
  const colorClassName = `breadcrumb${useId().replace(/^[A-Z0-9]gi/, "")}`;
  const { pages, expand, onClickPage } = props;
  const [showEllipses, setShowEllipses] = React.useState(true);

  const onClickEllipses = React.useCallback(() => {
    setShowEllipses(false);
  }, []);

  const colorStyles = {
    [`.${colorClassName}`]: {
      color: "text-secondary",
      textDecoration: "underline",
      textDecorationColor: "transparent",
      transition: "transition-color-medium"
    },
    [`&:hover .${colorClassName}`]: {
      color: "text-link-hover",
      textDecorationColor: "currentColor"
    },
    [`&:active .${colorClassName}`]: {
      color: "text-link-click",
      textDecorationColor: "currentColor"
    }
  };

  const renderEllipses = () => {
    return (
      <Target
        sx={expand ? colorStyles : {}}
        onClick={expand ? onClickEllipses : undefined}
      >
        <Text
          className={colorClassName}
          sx={{ paddingInlineEnd: "spacing-3" }}
          variant="title-2-regular"
          weight="regular"
        >
          ...
        </Text>
        <Text variant="title-2-regular">/</Text>
      </Target>
    );
  };

  const totalPages = pages.length;
  const breadCrumb = pages.map((page, index) => {
    if (showEllipses && totalPages > 3) {
      if (index === 0) {
        return (
          <React.Fragment key="ellipsis">{renderEllipses()}</React.Fragment>
        );
      }
      if (index > 0 && index < totalPages - 3) {
        return null;
      }
    }

    const isFirst = index === 0;
    const isLast = index === totalPages - 1;
    const fontWeight = isLast ? "medium" : "regular";

    const onClickNav = () => {
      if (!isLast) {
        onClickPage(page.pageId);
      }
    };

    return (
      <Target
        sx={{
          display: "flex",
          ...colorStyles
        }}
        key={page.pageId}
        onClick={isLast ? undefined : onClickNav}
      >
        <Text
          className={isLast ? undefined : colorClassName}
          sx={{
            color: "text-emphasis",
            paddingInlineStart: isFirst ? 0 : "spacing-3",
            paddingInlineEnd: "spacing-3"
          }}
          variant="title-2-regular"
          weight={fontWeight}
        >
          {page.title}
        </Text>
        {!isLast && <Text variant="title-2-regular">/</Text>}
      </Target>
    );
  });

  return (
    <Box
      as="nav"
      sx={{
        display: "flex",
        flexWrap: "wrap"
      }}
    >
      {breadCrumb}
    </Box>
  );
};
